import { supabase } from '@/integrations/supabase/client';

export interface ExportRow {
  onboarding_task_id: string;
  row_number: number;
  account_number?: string;
  property_name?: string;
  property_code?: string;
  unit_number?: string;
  commodity_type?: string;
  vendor_name?: string;
  meter_number?: string;
  gl_code?: string;
  service_address?: string;
  billing_address?: string;
  notes?: string;
  action?: 'EDIT' | 'DELETE' | 'UPDATE' | '';
  validation_status?: string;
  [key: string]: any;
}

/**
 * Export staged data to CSV with onboarding_task_id and Action column
 */
export async function exportStagedDataToCSV(
  batchId: string,
  includeActionColumn: boolean = true
): Promise<string> {
  const { data: stagedData, error } = await supabase
    .from('tum_onboarding_staged_data')
    .select('id, onboarding_task_id, row_number, mapped_data, validation_status, validation_issues')
    .eq('batch_id', batchId)
    .order('row_number', { ascending: true });

  if (error) {
    throw new Error(`Failed to load staged data: ${error.message}`);
  }

  if (!stagedData || stagedData.length === 0) {
    throw new Error('No data found to export');
  }

  // Convert to export format
  const exportRows: ExportRow[] = stagedData.map((row: any) => {
    const mapped = row.mapped_data || {};
    const exportRow: ExportRow = {
      onboarding_task_id: row.onboarding_task_id || row.id,
      row_number: row.row_number,
      account_number: mapped.account_number || '',
      property_name: mapped.property_name || '',
      property_code: mapped.property_code || '',
      unit_number: mapped.unit_number || '',
      commodity_type: mapped.commodity_type || '',
      vendor_name: mapped.vendor_name || '',
      meter_number: mapped.meter_number || '',
      gl_code: mapped.gl_code || '',
      service_address: mapped.service_address || '',
      billing_address: mapped.billing_address || '',
      notes: mapped.notes || '',
      validation_status: row.validation_status || 'pending',
    };

    if (includeActionColumn) {
      exportRow.action = ''; // Empty by default, user will fill
    }

    return exportRow;
  });

  // Convert to CSV
  const headers = Object.keys(exportRows[0]);
  const csvRows = [
    headers.join(','),
    ...exportRows.map(row =>
      headers.map(header => {
        const value = row[header] || '';
        // Escape commas and quotes
        if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      }).join(',')
    ),
  ];

  return csvRows.join('\n');
}

/**
 * Parse re-imported CSV and extract actions
 */
export function parseReimportCSV(csvContent: string): {
  rows: ExportRow[];
  errors: string[];
} {
  const lines = csvContent.split('\n').filter(line => line.trim());
  if (lines.length < 2) {
    return { rows: [], errors: ['CSV must have at least a header row and one data row'] };
  }

  const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
  const actionIndex = headers.indexOf('action');
  const taskIdIndex = headers.indexOf('onboarding_task_id');

  if (taskIdIndex === -1) {
    return { rows: [], errors: ['CSV must include "onboarding_task_id" column'] };
  }

  const rows: ExportRow[] = [];
  const errors: string[] = [];

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    const values: string[] = [];
    let currentValue = '';
    let inQuotes = false;

    // Parse CSV line handling quoted values
    for (let j = 0; j < line.length; j++) {
      const char = line[j];
      if (char === '"') {
        if (inQuotes && line[j + 1] === '"') {
          currentValue += '"';
          j++; // Skip next quote
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        values.push(currentValue.trim());
        currentValue = '';
      } else {
        currentValue += char;
      }
    }
    values.push(currentValue.trim()); // Add last value

    if (values.length !== headers.length) {
      errors.push(`Row ${i + 1}: Column count mismatch (expected ${headers.length}, got ${values.length})`);
      continue;
    }

    const row: ExportRow = {
      onboarding_task_id: values[taskIdIndex] || '',
      row_number: i,
    };

    // Map all columns
    headers.forEach((header, index) => {
      if (header !== 'onboarding_task_id' && header !== 'row_number') {
        row[header] = values[index] || '';
      }
    });

    // Validate action if present
    if (actionIndex !== -1) {
      const action = values[actionIndex].toUpperCase().trim();
      if (action && !['EDIT', 'DELETE', 'UPDATE', ''].includes(action)) {
        errors.push(`Row ${i + 1}: Invalid action "${action}". Must be EDIT, DELETE, UPDATE, or empty.`);
      } else {
        row.action = action as 'EDIT' | 'DELETE' | 'UPDATE' | '';
      }
    }

    if (!row.onboarding_task_id) {
      errors.push(`Row ${i + 1}: Missing onboarding_task_id`);
      continue;
    }

    rows.push(row);
  }

  return { rows, errors };
}

/**
 * Apply re-imported data with actions
 */
export async function applyReimportData(
  batchId: string,
  reimportRows: ExportRow[]
): Promise<{
  updated: number;
  deleted: number;
  errors: string[];
}> {
  const results = {
    updated: 0,
    deleted: 0,
    errors: [] as string[],
  };

  for (const row of reimportRows) {
    try {
      if (row.action === 'DELETE') {
        // Soft delete: mark as skipped
        const { error } = await supabase
          .from('tum_onboarding_staged_data')
          .update({
            import_status: 'skipped',
            validation_status: 'invalid',
          })
          .eq('onboarding_task_id', row.onboarding_task_id)
          .eq('batch_id', batchId);

        if (error) throw error;
        results.deleted++;
      } else if (row.action === 'EDIT' || row.action === 'UPDATE' || !row.action) {
        // Update mapped_data with new values
        const mappedData: Record<string, any> = {
          account_number: row.account_number,
          property_name: row.property_name,
          property_code: row.property_code,
          unit_number: row.unit_number,
          commodity_type: row.commodity_type,
          vendor_name: row.vendor_name,
          meter_number: row.meter_number,
          gl_code: row.gl_code,
          service_address: row.service_address,
          billing_address: row.billing_address,
          notes: row.notes,
        };

        // Remove empty values
        Object.keys(mappedData).forEach(key => {
          if (!mappedData[key]) delete mappedData[key];
        });

        const { error } = await (supabase as any)
          .from('tum_onboarding_staged_data')
          .update({
            mapped_data: mappedData,
            validation_status: 'pending', // Re-validate after update
          })
          .eq('onboarding_task_id', row.onboarding_task_id)
          .eq('batch_id', batchId);

        if (error) throw error;
        results.updated++;
      }
    } catch (error: any) {
      results.errors.push(`Row ${row.row_number} (${row.onboarding_task_id}): ${error.message}`);
    }
  }

  return results;
}

